import BaseEntity from '../base/BaseEntity';
import Matter from '../matter/Matter';
import FileUtil from '../../util/FileUtil';
import EnvUtil from '../../util/EnvUtil';
import { ShareType } from './ShareType';
import DateUtil from '../../util/DateUtil';
import SafeUtil from '../../util/SafeUtil';
import MessageBoxUtil from '../../util/MessageBoxUtil';
import Filter from '../base/filter/Filter';
import SortFilter from '../base/filter/SortFilter';
import InputFilter from '../base/filter/InputFilter';

export default class Share extends BaseEntity {
  name: string | null = null;
  shareType: ShareType = ShareType.MIX;
  userUuid: string | null = null;
  username: string | null = null;
  downloadTimes: number = 0;
  code: string | null = null;
  expireInfinity: boolean = false;
  expireTime: Date | null = null;
  targetSpaceNames?: string;  // 目标空间名称
  targetUserNames?: string;   // 目标用户名称
  //当前正在查看的文件夹
  dirMatter: Matter = new Matter();
  //当前share对应的matters
  matters: Matter[] = [];
  spaceUuid: string | null = null;
  sharePermission?: 'PREVIEW_ONLY' | 'FULL';
  // 私有分享相关字段
  shareScope: string = 'PUBLIC'; // PUBLIC, PRIVATE
  targetUserUuids: string[] = [];
  targetSpaceUuids: string[] = [];

  static URL_CREATE = '/api/share/create';
  static URL_CREATE_PRIVATE = '/api/share/create/private';
  static URL_BROWSE = '/api/share/browse';
  static URL_DELETE_BATCH = '/api/share/delete/batch';
  static URL_ZIP = '/api/share/zip';
  static URL_MATTER_PAGE = '/api/share/matter/page';

  constructor(reactComponent?: React.Component) {
    super(reactComponent);
  }

  assign(obj: any) {
    super.assign(obj);
    super.assignEntity('expireTime', Date);
    super.assignEntity('dirMatter', Matter);
    super.assignList('matters', Matter);
    super.assignList('targetUserUuids', String);
    super.assignList('targetSpaceUuids', String);
  }

  getTAG(): string {
    return 'share';
  }

  getFilters() {
    return [
      ...super.getFilters(),
      new SortFilter('查询类型', 'queryType'),
      new InputFilter('用户UUID', 'userUuid'),
      new InputFilter('创建时间', 'orderCreateTime'),
    ];
  }

  getForm() {
    return {
      name: this.name,
      uuid: this.uuid ? this.uuid : null,
      shareScope: this.shareScope,
      targetUserUuids: this.targetUserUuids,
      targetSpaceUuids: this.targetSpaceUuids,
    };
  }

  getIcon() {
    if (this.shareType === ShareType.MIX) {
      return FileUtil.getIcon('zip', false);
    } else {
      return FileUtil.getIcon(
        this.name,
        this.shareType === ShareType.DIRECTORY
      );
    }
  }

  getLink() {
    return EnvUtil.currentHost() + '/share/detail/' + this.uuid;
  }

  hasExpired() {
    if (this.expireInfinity) {
      return false;
    } else {
      if (this.expireTime) {
        return new Date(this.expireTime).getTime() < new Date().getTime();
      } else {
        return false;
      }
    }
  }

  //下载zip包
  downloadZip(rootUuid: string, puuid?: string) {
    window.open(
      `${EnvUtil.currentHost()}${Share.URL_ZIP}?shareUuid=${this.uuid}&code=${
        this.code
      }&puuid=${puuid}&rootUuid=${rootUuid}`
    );
  }

  /**
   * 创建公开分享
   * 直接使用 expireInfinity 和 expireTime 字段
   */
  httpCreate(
    matterUuids: string,
    successCallback?: (response?: any) => any,
    errorCallback?: (error?: any) => any
  ) {
    let that = this;

    const form: any = {
      matterUuids,
      spaceUuid: this.spaceUuid,
      expireInfinity: this.expireInfinity,
      shareScope: 'PUBLIC',
      sharePermission: this.sharePermission,
    };

    // 如果不是永久，则添加格式化的 expireTime
    if (!this.expireInfinity && this.expireTime) {
      const year = this.expireTime.getFullYear();
      const month = String(this.expireTime.getMonth() + 1).padStart(2, '0');
      const day = String(this.expireTime.getDate()).padStart(2, '0');
      const hours = String(this.expireTime.getHours()).padStart(2, '0');
      const minutes = String(this.expireTime.getMinutes()).padStart(2, '0');
      const seconds = String(this.expireTime.getSeconds()).padStart(2, '0');
      form.expireTime = `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    }

    this.httpPost(
      Share.URL_CREATE,
      form,
      function (response: any) {
        that.assign(response.data.data);
        SafeUtil.safeCallback(successCallback)(response);
      },
      errorCallback
    );
  }

  /**
   * 创建私有分享
   * 直接使用 expireInfinity 和 expireTime 字段
   */
  httpCreatePrivateShare(
    targetUserUuids: string[],
    targetSpaceUuids: string[],
    matterUuids: string,
    successCallback?: any,
    errorCallback?: any
  ) {
    let that = this;

    const form: any = {
      matterUuids: matterUuids,
      targetUserUuids: targetUserUuids.join(','),
      spaceUuid: this.spaceUuid,
      expireInfinity: this.expireInfinity,
      sharePermission: this.sharePermission,
    };

    if (targetSpaceUuids.length > 0) {
      form.targetSpaceUuids = targetSpaceUuids.join(',');
    }

    if (this.expireTime && !this.expireInfinity) {
      const year = this.expireTime.getFullYear();
      const month = String(this.expireTime.getMonth() + 1).padStart(2, '0');
      const day = String(this.expireTime.getDate()).padStart(2, '0');
      const hours = String(this.expireTime.getHours()).padStart(2, '0');
      const minutes = String(this.expireTime.getMinutes()).padStart(2, '0');
      const seconds = String(this.expireTime.getSeconds()).padStart(2, '0');
      form.expireTime = `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    }

    this.httpPost(
      Share.URL_CREATE_PRIVATE,
      form,
      function (response: any) {
        that.assign(response.data.data);
        SafeUtil.safeCallback(successCallback)(response);
      },
      function (error: any) {
        SafeUtil.safeCallback(errorCallback)(error);
      }
    );
  }

  httpDeleteBatch(
    uuids: string,
    successCallback?: () => any,
    errorCallback?: () => any
  ) {
    this.httpPost(
      Share.URL_DELETE_BATCH,
      { uuids: uuids },
      function (response: any) {
        SafeUtil.safeCallback(successCallback)(response);
      },
      errorCallback
    );
  }

  httpBrowse(
    puuid: string,
    rootUuid: string,
    successCallback?: () => any,
    errorCallback?: () => any
  ) {
    let that = this;

    const form = {
      puuid,
      rootUuid,
      shareUuid: this.uuid,
      code: this.code,
    };

    this.detailLoading = true;
    this.httpPost(
      Share.URL_BROWSE,
      form,
      function (response: any) {
        that.assign(response.data.data);
        that.detailLoading = false;
        SafeUtil.safeCallback(successCallback)(response);
      },
      function (errorMsg: any) {
        that.detailLoading = false;
        MessageBoxUtil.error(errorMsg);
        SafeUtil.safeCallback(errorCallback)(errorMsg);
      }
    );
  }

  // 判断是否为私有分享
  isPrivateShare(): boolean {
    return this.shareScope === 'PRIVATE';
  }

  // 获取分享范围显示文本
  getShareScopeText(): string {
    return this.isPrivateShare() ? '私有分享' : '公开分享';
  }

  // 获取目标用户数量
  getTargetUserCount(): number {
    return this.targetUserUuids ? this.targetUserUuids.length : 0;
  }

  // 获取目标空间数量
  getTargetSpaceCount(): number {
    return this.targetSpaceUuids ? this.targetSpaceUuids.length : 0;
  }
}